import React, {createRef} from 'react';
import Message from './Message';
import SearchCenterTile from './Components/SearchCenterTiles';
import SearchResultTile from './Components/SearchResultTile';

/**
 * Class to display the search assist messages.
 */
export default class SearchMessage extends Message {
    /**
     * Initializes a new instance of @see {SearchMessage}.
     * @param {Object} props The props object passed through to the message.
     */
    constructor(props) {
        super(props);

        this.state = {
            centerTiles: [],
            resultTiles: [],
            /**@type {HTMLElement} */
            resultTilesWrapper: undefined,
            /**@type {HTMLElement} */
            showMoreWrapper: undefined
        };
        /**
         * Ref to control the focus for screen readers when the see more results button is manipulated
         * by a keyboard. This will set focus on the first result tile, if they exist, and will allow
         * focus to be maintained in a logical order. Since the user is pressing the button, they should
         * be expecting to land on the first of the collection.
         * @type {Ref} The ref passed through to the focusable result tile after pressing see more results.
         */
        this.resultTileFocusRef = createRef();
        this.seeMoreResults = this.seeMoreResults.bind(this);
    }

    /**
     * Gets the returned center tiles and result tiles and adds them
     * to the display state. Also lookup the result tiles wrapper and show more
     * button wrapper based on the base of this component:
     * <div class="message message--bot search"></div>.
     */
    componentDidMount() {
        super.componentDidMount();
        const centerTiles = this.getCenterTiles();
        const resultTiles = this.getResultsTiles();

        this.setState({
            centerTiles: centerTiles,
            resultTiles: resultTiles,
            /**@type {HTMLElement} */
            resultTilesWrapper: this.base.querySelector('.search__results-tile-wrapper'),
            /**@type {HTMLElement} */
            showMoreWrapper: this.base.querySelector('.search__show-more-button')
        });
    }

    /**
     * Get the center tiles, if present, and create a collection of mapped tiles
     * with the appropriate values. If there are no center tiles present, then
     * return nothing. Note that despite being a seemingly singular entity,
     * the center tiles (graph_answer) returns as a collection. So we create a new
     * collection in case we get more than initially intended.
     * @returns The collection of mapped center tiles.
     */
    getCenterTiles() {
        if (!this.props.payload.graph_answer?.payload) {
            return;
        }
        // So much nesting and null/undefined checking...
        const centerPanel = this.props.payload.graph_answer.payload.center_panel;
        if (!centerPanel) {
            return;
        }
        //An array for not a specfically defined reason.
        const dataSets = centerPanel.data;
        const tiles = [];
        for (const dataSet of dataSets) {
            const tile = {
                snippetTitle: dataSet.snippet_title,
                snippetContent: dataSet.snippet_content,
                source: dataSet.source,
                url: dataSet.url
            }
            tiles.push(tile);
        }

        return tiles;
    }

    /**
     * Get the results tiles, if present, and create a collection of mapped tiles
     * with the appropriate values. If there are no result tiles present, then
     * return nothing.
     * @returns The mapped collection of results tiles.
     */
    getResultsTiles() {
        if (!this.props.payload.results || !this.props.payload.results.web) {
            return;
        }
        // So much nesting and null/undefined checking...
        const dataSets = this.props.payload.results.web.data;
        if (!dataSets) {
            return;
        }
        const resultTiles = [];
        for (const dataSet of dataSets) {
            const resultTile = {
                pageTitle: dataSet.page_title,
                pagePreview: dataSet.page_preview,
                pageUrl: dataSet.page_url,
                pageImageUrl: dataSet.page_image_url
            }
            resultTiles.push(resultTile);
        }

        return resultTiles;
    }

    /**
     * Toggles the show more button and the additional results tiles visually.
     */
    seeMoreResults() {
        this.state.showMoreWrapper.classList.toggle('hidden');
        this.state.resultTilesWrapper.classList.toggle('hidden');
        if (this.resultTileFocusRef.current) {
            this.resultTileFocusRef.current.focus();
        }
    }


    render() {
        let showMoreButtonBlock = [];
        if (this.state.resultTiles.length > 0) {
            const title = this.state.centerTiles[0] ? this.state.centerTiles[0].snippetTitle : 'your question';
            showMoreButtonBlock.push(
                <button aria-label={`See more results about ${title}.`} onClick={this.seeMoreResults}>See more results</button>
            );
        }
        return(
            <div className={this.generateSenderClassName('search')}>
                {this.generateSenderMessage()}
                <div className='search__center-tile-wrapper'>
                    {this.state.centerTiles.map((centerTile) => {
                            return <SearchCenterTile
                                key={centerTile}
                                snippetTitle={this.stripHtml(centerTile.snippetTitle)}
                                snippetContent={this.stripHtml(centerTile.snippetContent)}
                                source={centerTile.source}
                                url={centerTile.url}
                            />
                    })}
                </div>
                <div className='search__show-more-button'>
                    {showMoreButtonBlock}
                </div>
                <div className='search__results-tile-wrapper message-text hidden'>
                    {this.state.resultTiles.map((resultTile, index) => {
                        // Only pass through the ref to the first of N tiles.
                        return <SearchResultTile
                            key={resultTile}
                            pageTitle={this.stripHtml(resultTile.pageTitle)}
                            pagePreview={this.stripHtml(resultTile.pagePreview)}
                            pageUrl={resultTile.pageUrl}
                            pageImageUrl={resultTile.pageImageUrl}
                            focusRef={index === 0 ? this.resultTileFocusRef : undefined}
                        />
                    })}
                </div>
            </div>
        );
    }
}